/**
 * Small shared maths and colour helpers for the animation.
 *
 * These are the animation's own utilities — the rating engine has its own copies
 * in `@deckee/utilities` because the two must be able to move independently.
 */

import { RAIN_DAY_RGB, RAIN_NIGHT_RGB } from './constants';

export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Smoothstep between two edges. Returns 0 below `a`, 1 above `b`. */
export const smoothstep = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a));

  return t * t * (3 - 2 * t);
};

export const lerp = (a: number, b: number, f: number): number =>
  a + (b - a) * f;

/**
 * Deterministic pseudo-random in [0, 1). Used where a value must be stable
 * across frames (the star field, reduced-motion foam dabs) or repeatable for a
 * given seed (the hull churn jitter).
 */
export const hash = (x: number): number => {
  const s = Math.sin(x * 12.9898) * 43758.5453;

  return s - Math.floor(s);
};

/** `'1a2b3c'` → `[26, 43, 60]`. */
export const hexToRgb = (hex: string): number[] =>
  (hex.match(/\w\w/g) ?? []).map((pair) => parseInt(pair, 16));

/** Blend two `[r, g, b]` arrays. */
export const mixRgb = (a: number[], b: number[], t: number): number[] =>
  a.map((v, i) => v + (b[i] - v) * t);

export const rgbString = (a: number[]): string =>
  `rgb(${a.map((v) => Math.round(v)).join(',')})`;

/** Blend two hex colours and return a CSS `rgb()` string. */
export const lerpHex = (a: string, b: string, t: string | number): string =>
  rgbString(mixRgb(hexToRgb(a), hexToRgb(b), Number(t)));

/**
 * How much daylight there is at a fractional hour, 0.05 (night) to 1 (day).
 * Dawn runs 5–7am and dusk 6–8:30pm; outside that it is flat night or flat day.
 * The night floor is held through both ramps so scrubbing the timeline never
 * jumps at the dawn or dusk boundaries.
 */
export const daylightAt = (hour: number): number => {
  if (hour < 5 || hour >= 20.5) {
    return 0.05;
  }

  if (hour < 7) {
    return Math.max(0.05, (hour - 5) / 2);
  }

  if (hour > 18) {
    return Math.max(0.05, 1 - (hour - 18) / 2.5);
  }

  return 1;
};

/**
 * Canvas stroke style for raindrops. Night rain stays pale and soft; daytime
 * rain gains enough contrast to remain visible without overpowering the scene.
 */
export const rainDropStrokeStyle = (daylight: number, rain: number): string => {
  const t = clamp01(daylight);
  const intensity = clamp01(rain);
  const r = Math.round(lerp(RAIN_NIGHT_RGB[0], RAIN_DAY_RGB[0], t));
  const g = Math.round(lerp(RAIN_NIGHT_RGB[1], RAIN_DAY_RGB[1], t));
  const b = Math.round(lerp(RAIN_NIGHT_RGB[2], RAIN_DAY_RGB[2], t));
  // Keep drops subtle at night and only moderately stronger in daylight.
  const alpha = (0.22 + 0.28 * t) * intensity;

  return `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
};
